import React, { useCallback, useMemo, useState } from 'react';
import { AccountStatus, AppButton, Box, ErrorModal, Inscription, Preloader, PriceInput, WarningIcon } from '@common-fe/common-fe';
import { round, toNumber, toString } from 'lodash';

import { ModalWrapper } from '@/components';
import { SuccessModal, WarnModal } from '@/components/elements';
import { BankFields } from '@/modules/transaction/components/ReimburseMe/ConfirmationStep/Immediately/DirectDeposit/BankForm/useBankFields';
import { useGetBankAccountByIdQuery } from '@/modules/transaction/components/ReimburseMe/ConfirmationStep/Immediately/DirectDeposit/BankForm/useGetBankAccountsList.query';
import { useStore } from '@/modules/transaction/components/ReimburseMe/store/usePaymentMethods.store';
import { TransactionData } from '@/modules/transaction/transaction.types';
import { PaymentMethodType } from '@/modules/user/user.types';

import Informant from './Informant';
import PaymentMethodChoosing from './PaymentMethodChoosing';
import useStartRepaymentQuery from './useStartRepayment.query';

interface Props {
  onClose: () => void;
  data?: TransactionData;
  isRejectionFlow?: boolean;
}

const ClaimRepaymentModal: React.FC<Props> = ({ onClose, data, isRejectionFlow }) => {
  const [hasAccountPendingWarning, setHasAccountPendingWarning] = useState(false);
  const [hasWarning, setHasWarning] = useState(false);
  const [amount, setAmount] = useState(toString(data?.paidAmount));
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const selectedMethod = useStore((state) => state.selectedMethod);
  const { currentBankAccount, isLoading } = useGetBankAccountByIdQuery(selectedMethod?.accountId);
  const accountStatus = useMemo(() => currentBankAccount?.[BankFields.accountStatus], [currentBankAccount]);
  const onSuccess = useCallback(() => {
    setIsSubmitting(false);
    setIsSuccess(true);
  }, [setIsSuccess, setIsSubmitting]);
  const onError = useCallback((errorText?: string) => {
    setIsSubmitting(false);
    setError(errorText || '');
  }, [setError, setIsSubmitting]);
  const { startRepayment } = useStartRepaymentQuery(onSuccess, onError, data?.claimId);
  const onSubmit = useCallback(() => {
    const hasDirectDeposit = selectedMethod?.type === PaymentMethodType.DIRECT_DEPOSIT;

    if (hasDirectDeposit && accountStatus === AccountStatus.PENDING) {
      return setHasAccountPendingWarning(true);
    }
    if (hasDirectDeposit && accountStatus !== AccountStatus.OPEN) {
      return setError('Unexpected account status. Please contact administrator');
    }
  
    if (hasDirectDeposit) {
      setIsSubmitting(true);
      startRepayment({
        payment_method_id: selectedMethod?.id,
        repayment_amount: toNumber(amount),
      });
    } else {
      setHasWarning(true);
    }
  }, [
    amount,
    selectedMethod,
    startRepayment,
    setIsSubmitting,
    setHasWarning,
    accountStatus,
    setError,
    setHasAccountPendingWarning,
  ]);

  const amountToRepay = useMemo(() => round(((data?.paidAmount || 0) - (data?.approvedAmount || 0)), 2), [data]);

  if (hasAccountPendingWarning) {
    return (
      <WarnModal
        testId="account_on_verification"
        header="You cannot repay your claim yet"
        helptext="This payment method is still pending verification. You'll be able to use it as soon as your bank account is confirmed."
        cancelButtonNode={(
          <AppButton
            testId="ClaimRepaymentModal_account_on_verification_cancel"
            width="206px"
            buttonType="secondary"
            onClick={() => {
              setHasAccountPendingWarning(false);
              onClose();
            }}
          >
            Cancel
          </AppButton>
        )}
        submitButtonNode={
          <Box margin={{ left: 'spacing12' }}>
            <AppButton
              testId="ClaimRepaymentModal_account_on_verification_change_payment_method"
              width="206px"
            >
              Change Payment Method
            </AppButton>
          </Box>
        }
        visible
        iconNode={<WarningIcon style={{ height: '48px', width: '48px' }} />}
        onSetVisible={() => {
          setHasAccountPendingWarning(false);
        }}
      />
    );
  }

  if (hasWarning) {
    return (
      <WarnModal
        testId="claim-repayment"
        header="Add payment method."
        helptext="Payment method is required for claim repayment."
        cancelButtonNode={<AppButton width="160px" onClick={() => setHasWarning(false)}>Close</AppButton>}
        visible
        isPendingIcon
        onSetVisible={() => {
          setHasWarning(false);
        }}
      />
    );
  }

  if (isSuccess) {
    return (
      <SuccessModal
        testId="claim-repayment"
        header="Submitted!"
        buttonText="Got It!"
        buttonStyle={{ width: '160px' }}
        visible
        onSetVisible={() => {
          setIsSuccess(false);
          onClose();
        }}
      />
    );
  }

  if (error) {
    return (
      <ErrorModal
        testId="claim-repayment"
        header="Error!"
        helpText={error}
        visible
        onSetVisible={() => {
          setError('');
          onClose();
        }}
        buttonText="Close"
      />
    );
  }

  return (
    <ModalWrapper
      testId="ClaimRepayment"
      visible
      onSetVisible={onClose}
      title="Claim Repayment"
    >
      <Box
        pad="spacing24"
        background="module"
        round="moduleRound"
      >
        <Box
          round="12px"
          border={{
            color: 'border2',
            size: 'small',
          }}
          pad="spacing24"
          background="canvas"
        >
          <Box direction="row" align="center">
            <Inscription color="textBody" weight="bold" margin={{ right: 'spacing12' }} style={{ width: '244px' }}>
              Amount to repay
            </Inscription>
            <PriceInput
              testId="ClaimRepaymentModal"
              name="amountToRepay"
              value={toNumber(amount) > amountToRepay ? toString(amountToRepay) : amount}
              onChange={(val: string) => {
                if (amountToRepay && val && toNumber(val) > amountToRepay) {
                  return;
                }
                setAmount(val);
              }}
              {...toNumber(amount) ? {} : { errorText: 'Amount is required' }}
            />
          </Box>
        </Box>
      </Box>

      <Box
        pad="spacing24"
        background="module"
        round="moduleRound"
        margin={{ top: 'spacing24' }}
      >
        <Box
          round="12px"
          border={{
            color: 'border2',
            size: 'small',
          }}
          background="canvas"
        >
          <PaymentMethodChoosing isRejectionFlow={isRejectionFlow} />
        </Box>

        <Box
          round="12px"
          border={{
            color: 'border2',
            size: 'small',
          }}
          pad="spacing24"
          background="canvas"
          margin={{ top: 'spacing24' }}
        >
          <Informant />
        </Box>
      </Box>
      <Box margin={{ top: 'spacing24' }} direction="row" justify="end">
        <AppButton
          testId="ClaimRepaymentModal_cancel"
          width="160px"
          buttonType="secondary"
          onClick={onClose}
        >
          Cancel
        </AppButton>
        <Box margin={{ left: 'spacing12' }}>
          <AppButton
            testId="ClaimRepaymentModal_submit"
            width="160px"
            onClick={onSubmit}
            disabled={isSubmitting || isLoading || !toNumber(amount)}
          >
            {isSubmitting || isLoading ? <Preloader /> : 'Submit'}
          </AppButton>
        </Box>
      </Box>
    </ModalWrapper>
  );
};

export default ClaimRepaymentModal;
